<template>
  <div :class="['email-created-success', { 'inside-iframe': isAppLoadedInsideIframe }, modalClass]">
    <div v-if="deletedEmail" class="create-email-modal-banner">
      {{ $t("emailAccounts.deleteAndCreateNew.successfullyDeleted", [deletedEmail]) }}
    </div>
    <div v-else-if="noOfEmailAccountsCreated > 1" class="create-email-modal-banner">
      {{
        $t("emailAccounts.createEmail.numberOfEmailCreated", {
          count: noOfEmailAccountsCreated,
        })
      }}
    </div>
    <div class="success-banner">
      <!--
        Top: celebration emoji + "<email> is ready" heading. The action
        buttons (Access webmail / Back to home) live below the creds
        block in the new layout (the post-create success screen).
      -->
      <div class="success-banner__header">
        <h3 class="success-banner__title">
          <img
            class="success-banner__celebration"
            :src="'email-creation-success' | themeImage"
            alt=""
            aria-hidden="true"
          />
          {{ $t("emailAccounts.createEmail.readyHeading", { email }) }}
        </h3>
      </div>

      <div class="success-banner__creds">
        <div class="success-banner__row">
          <span class="font-600">{{ $t("emailAccounts.createEmail.emailLabel") }}</span
          >: {{ email }}
        </div>
        <div class="success-banner__row mt-8">
          <password-actions :password="password" :view="view"></password-actions>
        </div>
      </div>

      <!--
        Primary action: Access webmail (green). Secondary: Back to home
        (text-link styled). Reuses helper.openWebmail + the medusa event
        previously emitted from MailLinks; "Back to home" matches the
        breadcrumb "Home" target — pushes /home so the user lands on the
        cards homescreen in iframe / the redirect handler in standalone.
      -->
      <div class="success-banner__actions">
        <button type="button" class="button success-banner__webmail-btn" @click="onWebmailClick">
          {{ $t("emailAccounts.createEmail.accessWebmail") }}
        </button>
        <button type="button" class="success-banner__back-btn" @click="onBackToHome">
          {{ $t("emailAccounts.successScreen.backToHome") }}
        </button>
      </div>

      <!--
        Collapsible mobile/desktop-app download panel. Closed by default
        so it doesn't dominate the success screen. Content reuses the
        existing AndroidDownloadButton / AppleDownloadButton + brand QR.
      -->
      <mobile-desktop-app-accordion
        :is-first-mailbox="isFirstMailbox"
        :is-admin-mailbox="isAdminMailbox"
        :creation-source-hook="creationSourceHook"
        @close-modal="close && close()"
      />

      <!--
        Success-screen nudge owns both surfaces:
          - pending mode: existing pending entries with per-row Create/Buy
            buttons + an "Or add custom name" input below.
          - default mode: suggestion grid + CTA.
        The dedicated PendingAccountsList table is intentionally NOT
        rendered here — its compact layout lives inside the nudge to avoid
        two stacked "pending accounts" sections on the success screen.
      -->
      <success-screen-nudge
        @create="onNudgeCreate"
        @buy="onNudgeBuy"
        @review-pending="onReviewPending"
      />
    </div>
  </div>
</template>

<script>
import { getDomainName, getWebmailUrl, isAppLoadedInsideIframe, openWebmail } from "@/helpers";
import { mapGetters } from "vuex";
import PasswordActions from "../Utility/PasswordActions.vue";
import MobileDesktopAppAccordion from "./MobileDesktopAppAccordion.vue";
import SuccessScreenNudge from "./SuccessScreenNudge.vue";
import { trackSuccessDismissed } from "@/telemetry/mailboxNudgeEvents";

export default {
  name: "AccountCreateSuccess",
  components: {
    PasswordActions,
    MobileDesktopAppAccordion,
    SuccessScreenNudge,
  },
  props: {
    email: String,
    password: String,
    modalClass: {
      type: String,
      default: () => "",
      required: false,
    },
    deletedEmail: {
      type: String,
      required: false,
    },
    noOfEmailAccountsCreated: {
      type: Number,
      required: false,
    },
    close: Function,
    /** Analytics: whether the just-created mailbox was the order's first. */
    isFirstMailbox: {
      type: Boolean,
      default: false,
    },
    /** Analytics: whether the just-created mailbox is an admin mailbox. */
    isAdminMailbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Analytics: the source that launched the create flow (same value
     * new_email_creation_viewed sent) — forwarded to the app-download
     * accordion's expanded event.
     */
    creationSourceHook: {
      type: String,
      default: "create_email_account_tab",
    },
  },
  computed: {
    ...mapGetters({
      view: "view",
    }),
    ...mapGetters(["accountIdByEmail"]),
  },
  data() {
    const webmailUrlPlaceholder = getWebmailUrl().split("?")[0];
    return {
      webmailURL: webmailUrlPlaceholder,
      isAppLoadedInsideIframe: isAppLoadedInsideIframe(),
    };
  },
  methods: {
    postCloseModal(sourceHook) {
      this.close?.(sourceHook);
    },
    /**
     * Open the web client and fire the same `access_webmail_hook_click`
     * medusa event MailLinks used to emit from its header button.
     */
    onWebmailClick() {
      openWebmail(this.email);
      const eventObj = {
        eventName: "access_webmail_hook_click",
        /* the mailbox this event is about, as its own account id */
        account_id: this.accountIdByEmail(this.email),
        source_hook: "create_email_success",
      };
      this.medusaEvents.send(
        this.medusaEvents.createEventObj(eventObj, getDomainName(), "flock_order")
      );
    },
    /**
     * Secondary action under the creds block. Destination depends on
     * surface: in the iframe widget "Home" is the cards homescreen at
     * /home (matches the layout breadcrumb's Home target); in
     * standalone CP there is no /home shell, so the natural "home" for
     * this flow is the Email accounts listing.
     */
    onBackToHome() {
      trackSuccessDismissed({ sourceHook: "back to home" });
      this.close?.();
      this.$router.push(this.isAppLoadedInsideIframe ? "/home" : "/email-accounts");
    },
    /**
     * Bubble nudge events up to CreateEmail (this component's parent),
     * which owns the modal lifecycle. CreateEmail handles re-priming for
     * the next create or handing off to the buy/pending flows.
     */
    onNudgeCreate(usernames) {
      this.$emit("nudge-create", usernames);
    },
    onNudgeBuy(count) {
      this.$emit("nudge-buy", count);
    },
    onReviewPending() {
      this.$emit("nudge-review-pending");
    },
  },
};
</script>

<style lang="scss" scoped>
.email-created-success {
  .create-email-modal-banner {
    padding: 14px;
    border-radius: 4px;
    background: var(--lightBlueBgBanner);
    margin-bottom: 24px;
    font-size: 15px;
    font-weight: 600;
    line-height: 18px;
  }

  .note {
    border-radius: 5px;
    border: solid 1px var(--noteBordCol);
    padding: 16px 16px 16px 40px;
    box-sizing: border-box;
    margin: 24px 0;
    font-size: 14px;
    font-weight: normal;
    color: var(--primaryTextCol);
    background-color: var(--warningBgCol);
    background-image: url("../../assets/error-state-2.svg");
    background-position: 12px 14px;
    background-repeat: no-repeat;
    width: auto;
    p {
      margin-bottom: 12px;
      color: var(--genericTextCol);
    }
    a {
      width: auto;
      color: var(--linkBtnCol);
    }
  }
}

// In the iframe widget the success screen already sits on the modal
// card's surface, so the `var(--tabBgCol)` fill that CreateEmail's
// (unscoped) `.success-banner` rule applies reads as a doubled-up
// background. Drop it here — standalone keeps the fill. Specificity
// (0,3,0) beats the global `.success-banner` (0,1,0).
.email-created-success.inside-iframe .success-banner {
  background: transparent;
}

// The section divider between the app-download panel and the nudge is the
// `border-top` on `.success-screen-nudge`. That section lives inside
// `.modal-content-wrapper` (max-width 672px, left-aligned, 24px side padding),
// so the border only spans the 672 content column. In the iframe the card
// itself spans the full iframe width (flush-left, no padding), so the divider
// should reach edge-to-edge. Swap the content-width border for a full-bleed
// `::before` line — only the line spans the viewport; the section's rows stay
// in the 672 column. Standalone CP keeps the plain 672-wide border.
.email-created-success.inside-iframe ::v-deep .success-screen-nudge {
  border-top: 0;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0; // where the border used to sit (24px padding-top below it)
    width: 100vw; // span the full iframe width
    border-top: 1px solid var(--lineSepaCol);
  }
}

.success-banner {
  &__header {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 22px;
    font-weight: 700;
    color: var(--primaryTextCol);
    margin: 0;
    word-break: break-word;
  }

  &__celebration {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }

  &__row {
    font-size: 14px;
    color: var(--primaryTextCol);
    line-height: 22px;
  }

  // Primary [Access webmail] + secondary [Back to home] action pair.
  // Sits between the creds block and the app-download accordion.
  &__actions {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0 4px;
  }

  // Inherits the theme's primary button color (--primaryBtnCol) via the
  // shared `.button` class so the action recolors per partner theme
  // (Titan blue, CrazyDomains green, hostGator blue, etc.) instead of
  // hard-coding the screenshot's green.
  &__webmail-btn {
    min-width: 160px;
  }

  // Text-style secondary action — no border, sits inline next to the
  // primary button.
  &__back-btn {
    background: transparent;
    border: none;
    color: var(--primaryTextCol);
    font-size: 14px;
    font-weight: 600;
    padding: 10px 12px;
    cursor: pointer;
    &:hover,
    &:focus {
      color: var(--linkBtnCol);
    }
    &:focus {
      outline: 2px solid var(--linkBtnCol);
      outline-offset: 2px;
    }
  }
}
</style>
